import { ReactNode, createContext, useContext, useState } from 'react';


// Base tenant type without the "all selected" flag, used by consumers of setSelectedTenant
type TenantObject = {
  id: number | null;
  name: string;
  slug: string;
}

type TenantObjectWithAllSelected = TenantObject & {
  isAllSelected: boolean;
}

type PartnerContextProps = {
  selectedTenant: TenantObjectWithAllSelected;
  setSelectedTenant: (tenant: TenantObject) => void;
  reset: () => void;
  setAllSelected: () => void;
}

type PartnerProviderProps = {
  children: ReactNode;
}

const AuthContext = createContext<PartnerContextProps | undefined>(undefined);

const PartnerContext = ({ children }: PartnerProviderProps) => {
  const [selectedTenant, setSelectedTenant] = useState<TenantObjectWithAllSelected>({
    id: null,
    name: '',
    slug: '',
    isAllSelected: false,
  });

  const contextValue: PartnerContextProps = {
    selectedTenant,
    // Selecting a specific tenant always resets the "all" flag
    setSelectedTenant: (tenant: TenantObject) => setSelectedTenant({ ...tenant, isAllSelected: false }),
    // Marks "all tenants" mode while preserving the current tenant for features that need a single tenant fallback (e.g. reports list, dashboard)
    setAllSelected: () => setSelectedTenant((prev) => ({ ...prev, isAllSelected: true })),
    reset: () => setSelectedTenant({ id: null, name: '', slug: '', isAllSelected: false }),
  };

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};
const usePartnerContext = (): PartnerContextProps => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('Something went wrong.');
  }
  return context;
};

export { usePartnerContext, PartnerContext };
